import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_RECURRING_JOB } from '../../../graphql/queries';
import { 
  convertTo24HourFormat 
} from '../utils/timeUtils';
import { 
  createRecurringJobInput 
} from '../utils/jobUtils';

/**
 * Custom hook for recurring job creation functionality
 * @param {Function} onSuccess - Callback function on successful recurring job creation
 * @param {Function} onError - Callback function on recurring job creation error
 * @returns {Object} Recurring job creation state and functions
 */
export default function useRecurringJob(onSuccess, onError) {
  const [isCreating, setIsCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [createRecurringJob, { loading: createRecurringLoading }] = useMutation(CREATE_RECURRING_JOB, {
    onCompleted: (data) => {
      console.log('Recurring job created successfully:', data);
      setSuccessMessage(`Recurring job "${data.createRecurringJob.name}" created successfully!`);
      onSuccess?.(data.createRecurringJob);
    },
    onError: (error) => {
      console.error('Error creating recurring job:', error);
      const errorMsg = error.message || 'Failed to create recurring job. Please try again later.';
      setErrorMessage(errorMsg);
      onError?.(errorMsg);
    }
  });

  /**
   * Create a recurring job
   * @param {Object} formData - Form data for recurring job creation
   * @param {Object} selectedProperty - Selected property object for name generation
   */
  const createRecurring = async (formData, selectedProperty) => {
    setIsCreating(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Convert time format
      const formattedStartTime = convertTo24HourFormat(formData.startTime);
      
      console.log('Creating recurring job with input:', {
        ...formData,
        startTime: formattedStartTime
      });

      const recurringJobInput = createRecurringJobInput({
        ...formData,
        startTime: formattedStartTime
      }, selectedProperty);

      await createRecurringJob({
        variables: {
          input: recurringJobInput
        }
      });
    } catch (error) {
      console.error('Error in createRecurring:', error);
      setErrorMessage(error.message);
      onError?.(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Clear success and error messages
   */
  const clearMessages = useCallback(() => {
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  return {
    // State
    isCreating: isCreating || createRecurringLoading,
    successMessage,
    errorMessage,
    
    // Actions
    createRecurring,
    clearMessages
  };
} 